import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ProjectIssuanceConstants, UserType } from "../../../constants";
import { getProjectPhaseCounts } from "../../../constants/project-phase";
import { TaskConstants } from "../../../constants/workflow/TaskConstants";
import {
  CursorChangeProps,
  FilterOperator,
  InventoryBreakdownMeasurement,
  ResultType,
  UpcomingMilestonePropsWithKey,
} from "../../../models";
import {
  getAggregateIssuances,
  getAggregateWorkflowTasks,
  getProjectAggregate,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../service/query";
import { ResultData, Status } from "../../../service/Shared";
import { useTaskPanelWorkflow } from "../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../useAuth";
import { buildInventoryBreakdownMeasurements, flattenObject, getTaskParentObjectNavigation } from "../../../utils";
import {
  ChartData,
  DashboardMapPin,
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  OverdueTaskProps,
  Toast,
  useProjectMapPins,
} from "../../../widget";

const UNITS_LOAD_ERROR_TOAST = {
  key: "development-manager-dashboard-units-load-error",
  message: "Failed to load your project overview",
};

const MILESTONES_LOAD_ERROR_TOAST = {
  key: "development-manager-dashboard-milestones-load-error",
  message: "Failed to load upcoming milestones",
};

interface UseDevelopmentManagerDashboardReturnData {
  phaseChartData: ChartData;
  unitMeasurementTotals: InventoryBreakdownMeasurement[];
  isLoadingUnitMeasurementTotals: boolean;
  selectedStatuses: string[];
  setSelectedStatuses: (statuses: string[]) => void;
  isLoading: boolean;
  isLoadingTasks: boolean;

  columnsTasks: DataGridColumnDefinition[];
  columnsTeamTasks: DataGridColumnDefinition[];
  tasksDataLoad: DataGridCursorDataLoadEventHandler;
  teamTasksDataLoad: DataGridCursorDataLoadEventHandler;
  currentUserType: UserType;
  overdueTasks: OverdueTaskProps;
  isOverdueTasksPanelOpen: boolean;
  setIsOverdueTasksPanelOpen: (isOpen: boolean) => void;

  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  isLoadingMilestones: boolean;
  hasMilestonesError: boolean;
  projectPins: DashboardMapPin[];
  isLoadingProjectPins: boolean;
  isMapExpanded: boolean;
  setIsMapExpanded: (isExpanded: boolean) => void;
  isUpcomingMilestonesPanelOpen: boolean;
  setIsUpcomingMilestonesPanelOpen: (isOpen: boolean) => void;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;

  isTasks: boolean;
  hasTasks: (isTasks: boolean) => void;

  isTeamTasks: boolean;
  hasTeamTasks: (isTeamTasks: boolean) => void;

  isYourTasksPanelOpen: boolean;
  setIsYourTasksPanelOpen: (isOpen: boolean) => void;

  isTeamTasksPanelOpen: boolean;
  setIsTeamTasksPanelOpen: (isOpen: boolean) => void;

  handleRefresh: () => void;
  refreshTasks: boolean;

  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;
}

const columnsTasks: DataGridColumnDefinition[] = [
  {
    // The task name opens the task panel, which is what the removed "View" icon column used to do.
    key: "taskLink",
    name: "Task",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    minWidth: 200,
  },
  {
    // The uniform parent (Project / Deal / Landholding), which a general task has too.
    key: "parentObjectLink",
    name: "Project / Landholding",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    minWidth: 140,
  },
  {
    key: "status",
    name: "Status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
  },
];

const columnsTeamTasks: DataGridColumnDefinition[] = [
  {
    // The task name opens the task panel, which is what the removed "View" icon column used to do.
    key: "taskLink",
    name: "Task",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    minWidth: 200,
  },
  {
    // The uniform parent (Project / Deal / Landholding), which a general task has too.
    key: "parentObjectLink",
    name: "Project / Landholding",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    minWidth: 140,
  },
  {
    key: "status",
    name: "Status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    minWidth: 100,
  },
  {
    // Team tasks span organisations, so this table keeps the owning one after the four shared columns.
    key: "ownerOrganisation.name",
    name: "Owner",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
];

export const useDashboard = (): UseDevelopmentManagerDashboardReturnData => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentDevelopmentManagerUuid, currentUserType, user } = useAuth();

  const [phaseChartData, setPhaseChartData] = useState<ChartData>({});
  const [unitMeasurementTotals, setUnitMeasurementTotals] = useState<InventoryBreakdownMeasurement[]>([]);
  /*
    Tracked separately from the dashboard's own `isLoading`, which is cleared by the project-phase
    request: that can land before this one, and the card would otherwise announce "no units" while
    the aggregate was still in flight.
  */
  const [isLoadingUnitMeasurementTotals, setIsLoadingUnitMeasurementTotals] = useState(true);
  /* Lives only as long as the page is open — not persisted, and local to this dashboard. */
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    ProjectIssuanceConstants.ISSUANCE_STATUS_HUB_DEFAULTS
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);

  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(true);
  const [hasMilestonesError, setHasMilestonesError] = useState(false);
  /** Identifies the newest milestone fetch, so a slower earlier one cannot overwrite its result. */
  const milestonesRequestId = useRef(0);
  const [overdueTasks, setOverdueTasks] = useState<OverdueTaskProps>();
  const [isOverdueTasksPanelOpen, setIsOverdueTasksPanelOpen] = useState(false);
  const [isUpcomingMilestonesPanelOpen, setIsUpcomingMilestonesPanelOpen] = useState(false);
  /** Identifies the newest units fetch, so a slower earlier one cannot overwrite its figure. */
  const unitsRequestId = useRef(0);

  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const [isYourTasksPanelOpen, setIsYourTasksPanelOpen] = useState(false);

  /* Drives the dashboard layout as well as the map: expanded, the map takes the whole row
     and the tasks card reflows underneath it */
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  const [isTeamTasksPanelOpen, setIsTeamTasksPanelOpen] = useState(false);

  const [isTasks, hasTasks] = useState(false);
  const [isTeamTasks, hasTeamTasks] = useState(false);

  const { projectPins, isLoadingProjectPins } = useProjectMapPins(currentUserType);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const workflow = useTaskPanelWorkflow(selectedPanelTask?.workflowUuid);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  /*
    Its own callback rather than part of `fetchChartsData`: it re-runs on every status change, and
    `fetchChartsData` is what clears `isLoading` for the whole dashboard — sharing it would drop the
    project-phase card back into its skeleton each time the user changed a chip.
  */
  const fetchUnitsData = useCallback(async () => {
    /*
      The status chips are quick to click, so two fetches can overlap. Only the newest may touch the
      card: an older one landing last would leave a figure that does not match the selection.
    */
    unitsRequestId.current += 1;
    const requestId = unitsRequestId.current;
    const isCurrentRequest = (): boolean => requestId === unitsRequestId.current;
    setIsLoadingUnitMeasurementTotals(true);

    try {
      const response = await getAggregateIssuances({
        filters: [
          /*
            No selection means no status filter, which the server reads as every status - the card
            then shows the organisation's whole inventory rather than nothing. An empty `in` list is
            rejected outright, so the entry has to be absent rather than empty.
          */
          ...(selectedStatuses.length > 0
            ? [
                {
                  filterBy: "issuanceStatus",
                  filterOperator: "in" as FilterOperator,
                  filterValue: `[${selectedStatuses.join(",")}]`,
                },
              ]
            : []),
        ],
        groupBy: ["unitMeasurement"],
        isReadableProject: true,
        paging: { beforeCursor: null, afterCursor: null, limit: 100 },
        sort: [],
        filter: { results: {} },
      });

      if (response.status !== Status.Success || !response.data) {
        if (isCurrentRequest()) Toast.error(UNITS_LOAD_ERROR_TOAST);
        return;
      }

      if (!isCurrentRequest()) {
        return;
      }

      setUnitMeasurementTotals(buildInventoryBreakdownMeasurements(response.data.results));
    } catch {
      if (isCurrentRequest()) Toast.error(UNITS_LOAD_ERROR_TOAST);
    } finally {
      // Whatever happened, the card stops saying "Loading..." - a failure is reported by the Toast.
      if (isCurrentRequest()) setIsLoadingUnitMeasurementTotals(false);
    }
  }, [selectedStatuses]);

  const fetchChartsData = useCallback(async () => {
    if (!currentDevelopmentManagerUuid) {
      throw new Error(`CurrentDevelopmentManagerUuid has no value. ${currentDevelopmentManagerUuid}`);
    }
    const phaseAggregateResponse = await getProjectAggregate({
      filterBy: "developmentManager",
      filterOperator: "eq",
      filterValue: currentDevelopmentManagerUuid,
      groupBy: "cachedPhase",
      aggregation: "count",
      aggregate: "id",
    });

    // If no data initialise with 0
    if (phaseAggregateResponse.status === Status.Success && phaseAggregateResponse.data) {
      setPhaseChartData(getProjectPhaseCounts(phaseAggregateResponse.data));
    }

    const tasks = await getAggregateWorkflowTasks({
      activityUuid: null,
      phase: null,
      projectUuid: null,
      workflowUuid: null,
      dealUuid: null,
    });
    setOverdueTasks({
      amount: tasks.data?.overdueTasksCount ? tasks.data?.overdueTasksCount.toString() : "0",
      label: "Overdue tasks",
    });
  }, []);

  /*
    Its own loader rather than a tail of `fetchChartsData`: the milestone list is an independent
    query, and sharing that function meant a failure anywhere in the charts path — including the
    `currentDevelopmentManagerUuid` throw above — skipped the milestone state entirely, leaving the
    card on its skeletons. It also lets the request be identified, so overlapping refreshes resolve
    in the right order.
  */
  const fetchMilestones = useCallback(async () => {
    milestonesRequestId.current += 1;
    const requestId = milestonesRequestId.current;
    const isCurrentRequest = (): boolean => requestId === milestonesRequestId.current;

    setIsLoadingMilestones(true);
    setHasMilestonesError(false);
    try {
      const result = await searchWorkflowTasks({
        paging: {
          limit: 4,
          afterCursor: null,
          beforeCursor: null,
        },
        filter: {
          results: {
            isMilestone: {
              operator: "eq",
              value: true,
            },
            deadlineDate: {
              operator: "neq",
              value: null,
            },
            status: {
              operator: "neq",
              value: TaskConstants.TASK_STATUS_COMPLETE,
            },
          },
        },
        sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      });
      if (!isCurrentRequest()) {
        return;
      }
      if (result.status !== Status.Success || result.data === undefined) {
        setHasMilestonesError(true);
        Toast.error(MILESTONES_LOAD_ERROR_TOAST);
        return;
      }
      setUpcomingMilestones(
        result.data.results.map((task) => ({
          key: task.uuid,
          name: task.name,
          deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
          status: task.status,
          assigneeName: task.assignee?.name ?? undefined,
          // The uniform parent, so the name and its link always refer to the same object — and
          // general milestone tasks, which have no `workflowObject*`, get one too.
          project: task.parentObjectName ?? undefined,
          onProjectClick: getTaskParentObjectNavigation(
            task.parentObjectType,
            task.parentObjectUuid,
            currentUserType,
            navigate,
            { goBackText: "Back to dashboard", from: location.pathname }
          ),
          onClick: () => {
            setSelectedPanelTask(task);
            setShowWorkflowTaskPanel(true);
          },
        }))
      );
    } catch {
      if (isCurrentRequest()) {
        setHasMilestonesError(true);
        Toast.error(MILESTONES_LOAD_ERROR_TOAST);
      }
    } finally {
      if (isCurrentRequest()) {
        setIsLoadingMilestones(false);
      }
    }
  }, [currentUserType, navigate, location.pathname]);

  useEffect(() => {
    fetchChartsData().finally(() => {
      setIsLoading(false);
    });
  }, [fetchChartsData, refreshTasks]);

  /*
    Deliberately not tied to `isLoading`: a status change refetches only the units figure, and the
    card keeps showing the previous number until the new one lands.
  */
  useEffect(() => {
    fetchUnitsData();
  }, [fetchUnitsData]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones, refreshTasks]);

  const formatData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.taskLink = {
          text: el.name,
          action: () => {
            setSelectedPanelTask(el);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        /*
          The uniform parent, so the name and its link always refer to the same object — and general
          tasks, which have no `workflowObject*`, get one too. Without an action the formatter renders
          plain text, which is right for a parent this role cannot open.
        */
        result.parentObjectLink = {
          text: el.parentObjectName ?? "",
          action: getTaskParentObjectNavigation(el.parentObjectType, el.parentObjectUuid, currentUserType, navigate, {
            goBackText: "Back to dashboard",
            from: location.pathname,
          }),
        } as DataGridButtonLinkCellFormatterData;

        return result;
      }) || [],
    [currentUserType, navigate, location.pathname]
  );

  const tasksDataLoad: DataGridCursorDataLoadEventHandler = async ({ paging, filtering }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 4,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsLoadingTasks(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.assignee = {
      uuid: {
        operator: "eq",
        value: user?.userUuid || "",
      },
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    await searchWorkflowTasks({
      paging: {
        limit: 5,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: { results: filterCriteria },
      sort: [
        {
          key: "deadlineDate" as "results.deadlineDate",
          direction: "asc",
        },
      ],
    })
      .then((response) => {
        const formatted = formatData(response.data);
        // Set isTasks to true if there are any tasks, false otherwise
        hasTasks(formatted.length > 0);

        data = {
          resultData: formatted,
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });
    return data;
  };

  const teamTasksDataLoad: DataGridCursorDataLoadEventHandler = async ({
    paging,
    filtering,
    sorting,
  }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 6,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsLoadingTasks(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }
    sorting.forEach((s) => {
      switch (s.key) {
        case "avatar":
          // eslint-disable-next-line no-param-reassign
          s.key = "assignee.name";
          break;
        default:
          break;
      }
    });

    await searchWorkflowTasks({
      paging: {
        limit: 6,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      filter: { results: filterCriteria },
    })
      .then((response) => {
        const formatted = formatData(response.data);
        // Set isTeamTasks to true if there are any tasks, false otherwise
        hasTeamTasks(formatted.length > 0);

        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });
    return data;
  };

  return {
    phaseChartData,
    unitMeasurementTotals,
    isLoadingUnitMeasurementTotals,
    selectedStatuses,
    setSelectedStatuses,

    isLoading,
    isLoadingTasks,

    columnsTasks,
    columnsTeamTasks,

    tasksDataLoad,
    teamTasksDataLoad,

    currentUserType,
    upcomingMilestones,
    projectPins,
    isLoadingProjectPins,
    isMapExpanded,
    setIsMapExpanded,

    overdueTasks: overdueTasks || { amount: "0", label: "Overdue tasks" },
    isOverdueTasksPanelOpen,
    setIsOverdueTasksPanelOpen,

    isLoadingMilestones,
    hasMilestonesError,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,

    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,

    isTeamTasksPanelOpen,
    setIsTeamTasksPanelOpen,

    isTasks,
    hasTasks,

    isTeamTasks,
    hasTeamTasks,

    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
  };
};
